import { ReactNode, createContext, useContext } from 'react';

import { wacToken } from '../foundations';

interface ThemeContextValue {
	isDarkTheme: boolean;
	isDevTheme: boolean;
	isAwf1Theme: boolean;
}

const ThemeContext = createContext<ThemeContextValue>({
	isDarkTheme: false,
	isDevTheme: false,
	isAwf1Theme: false,
});

export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
	isDarkEnabled: boolean;
	isDevEnabled?: boolean;
	isAwf1Enabled?: boolean;
	children: ReactNode;
}

/**
 * Text colours for a subtree rendered on a dark surface.
 *
 * Spread this onto the element that paints the dark background. `TypographyStyles` colours
 * `p`, `h1`-`h6` and `.body-*` with `var(--wac-color-body|heading)` via *descendant* selectors,
 * and an inherited `color` always loses to a declaration that matches an element directly — so
 * setting `color` on a dark container leaves any nested `<p>` (including caller-supplied
 * children) on the light-surface colour and effectively invisible. Overriding the two custom
 * properties feeds TypographyStyles' own rules instead of competing with them, so there is no
 * specificity contest and arbitrary children come out right.
 *
 * Covers text only — the two properties above are the whole ambient colour surface. Children
 * that paint their own non-text colour (icon fills, borders) still need telling; read
 * `useTheme().isDarkTheme` for that rather than adding a bespoke `theme` prop.
 */
export const onDarkSurfaceText: React.CSSProperties = {
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
	['--wac-color-heading' as string]: wacToken('color.neutral.light0'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
	['--wac-color-body' as string]: wacToken('color.neutral.light0'),
};

/**
 * The inverse of {@link onDarkSurfaceText}: text colours for a subtree rendered on a light
 * surface. Needed when a component paints a light surface *inside* a dark region, where the
 * ambient custom properties are already set for dark and would otherwise bleed through —
 * e.g. a white popup opened from a trigger sitting on a dark section.
 */
export const onLightSurfaceText: React.CSSProperties = {
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
	['--wac-color-heading' as string]: wacToken('color.primary.black'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
	['--wac-color-body' as string]: wacToken('color.primary.black'),
};

const darkThemeStyles: React.CSSProperties = {
	...onDarkSurfaceText,
	backgroundColor: wacToken('color.neutral.dark100'),
};

const darkAwf1ThemeStyles: React.CSSProperties = {
	...darkThemeStyles,
	backgroundColor: wacToken('color.primary.black'),
};

export const ThemeProvider = ({
	isDarkEnabled,
	isDevEnabled = false,
	isAwf1Enabled = false,
	children,
}: ThemeProviderProps) => {
	return (
		<ThemeContext.Provider
			value={{ isDarkTheme: isDarkEnabled, isDevTheme: isDevEnabled, isAwf1Theme: isAwf1Enabled }}
		>
			<div
				style={isDarkEnabled ? (isAwf1Enabled ? darkAwf1ThemeStyles : darkThemeStyles) : undefined}
			>
				{children}
			</div>
		</ThemeContext.Provider>
	);
};
